import type { AuditEntry } from './api/types';

/**
 * Turning an audit record into something a person can read (D32).
 *
 * The screen this feeds is opened by somebody asking "who changed this, and when", usually months
 * after the change. So the wording is a sentence about a person rather than a field name and a
 * payload: "Jane Editor changed the consent notice", not "org_settings.update". Anything the
 * wording cannot recognise falls back to saying the raw action out loud, because a record nobody
 * has written a sentence for is still a record somebody needs to see.
 *
 * Everything here returns text. Nothing here returns markup, and nothing that uses it may render
 * its result as HTML: consent wording is stored as sanitised markup and an audit value can hold
 * anything a publisher typed into a settings box.
 */

/** The marker `auditWrite` puts inside a value it cut, which this screen says in its own words. */
const TRUNCATION_MARKER = ' [truncated]';

/**
 * What somebody did, as the second half of a sentence starting with their name.
 *
 * Written per action rather than assembled from the action id, because "team.invite_cancel" and
 * "cancelled the invitation to Sam" are not the same sentence and only one of them answers a
 * question a year later.
 */
export function auditSentence(entry: AuditEntry): string {
  const target = entry.target.name ?? entry.target.id;

  switch (entry.action) {
    case 'org_settings.update':
      return 'changed the publisher settings';
    case 'brand.create':
      return `created the brand ${target}`;
    case 'brand.update':
      return `changed the brand ${target}`;
    case 'brand.delete':
      return `deleted the brand ${target}`;
    case 'team.invite':
      return `invited ${target}`;
    case 'team.invite_cancel':
      return `cancelled the invitation to ${target}`;
    case 'team.invite_accept':
      return 'joined the team';
    case 'api_key.create':
      return `created the API key ${target}`;
    case 'api_key.revoke':
      return `revoked the API key ${target}`;
    case 'webhook.set':
      return 'set the newsletter webhook';
    case 'webhook.rotate_secret':
      return 'generated a new webhook secret';
    case 'webhook.delete':
      return 'deleted the newsletter webhook';
    case 'subscribers.export':
      return 'exported the subscriber list';
    default:
      // An action this build has no sentence for. Better said plainly than left off the screen.
      return `made a change recorded as ${entry.action}${target === '' ? '' : `, on ${target}`}`;
  }
}

/**
 * A note under the sentence for records that have no fields to show.
 *
 * A rotation moves the one thing that cannot be written down, so an empty change list there means
 * something quite specific and the screen says it rather than showing a blank.
 */
export function auditNote(entry: AuditEntry): string | null {
  if (entry.changes.length > 0) return null;
  if (entry.action === 'webhook.rotate_secret') {
    return 'The secret itself is never recorded, so there is nothing else to show.';
  }
  return 'No field values were recorded with this change.';
}

const FIELD_LABELS: Record<string, string | undefined> = {
  name: 'Name',
  domains: 'Domains',
  theme: 'Theme',
  consent_text: 'Consent notice',
  newsletter_opt_in: 'Newsletter signup',
  subscriber_access: 'Who can see subscribers',
  url: 'Address',
  active: 'Active',
  role: 'Role',
  scopes: 'What it can read',
  revoked_at: 'Revoked',
  brand_id: 'Brand',
  interaction_id: 'Interaction',
  status: 'Status',
  from: 'From',
  to: 'To',
  accent_colour: 'Accent colour',
  text_colour: 'Text colour',
  background_colour: 'Background colour',
  corner_radius: 'Corner radius',
  show_brand_credit: 'Brand credit',
};

/** A field name in a publisher's words, falling back to the stored name spelled out. */
export function auditFieldLabel(field: string): string {
  const known = FIELD_LABELS[field];
  if (known !== undefined) return known;
  const words = field.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * One value as text, plus whether it is a value at all.
 *
 * `empty` is what lets the screen show "nothing set" differently from a thing somebody typed: a
 * setting going from nothing to something reads very differently from one being edited, and the
 * two must not look the same.
 */
export interface AuditValueText {
  readonly text: string;
  readonly empty: boolean;
}

/**
 * What a stored value looked like, said in words.
 *
 * Values arrive as whatever the setting held: a string for a consent notice, a list for domains,
 * an object for a theme. An object becomes its fields read out ("Accent colour: #6b4ee6, Corner
 * radius: 12") rather than JSON, because a person reading this a year later is reading a sentence
 * and not a payload.
 */
export function auditValueText(value: unknown, truncated = false): AuditValueText {
  const base = describe(value);
  if (!truncated) return base;
  // The stored value carries its own marker as well as the flag. The screen says it once, in its
  // own words, so the marker is taken off what is shown.
  const text = base.text.endsWith(TRUNCATION_MARKER)
    ? base.text.slice(0, -TRUNCATION_MARKER.length)
    : base.text;
  return { text, empty: false };
}

function describe(value: unknown): AuditValueText {
  if (value === null || value === undefined) return { text: 'nothing set', empty: true };

  if (typeof value === 'string') {
    if (value === '') return { text: 'empty', empty: true };
    return { text: value, empty: false };
  }

  if (typeof value === 'boolean') return { text: value ? 'yes' : 'no', empty: false };
  if (typeof value === 'number') return { text: String(value), empty: false };

  if (Array.isArray(value)) {
    if (value.length === 0) return { text: 'none', empty: true };
    return { text: value.map(simple).join(', '), empty: false };
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return { text: 'nothing set', empty: true };
    return {
      text: entries.map(([key, held]) => `${auditFieldLabel(key)}: ${simple(held)}`).join(', '),
      empty: false,
    };
  }

  return { text: String(value), empty: false };
}

/** One value inside a list or an object, where there is no room for a sentence. */
function simple(value: unknown): string {
  if (value === null || value === undefined) return 'nothing';
  if (typeof value === 'string') return value === '' ? 'empty' : value;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return String(value);
  return describe(value).text;
}
